// CLOUD: the web app's file system (2026-09-25, Tony; replaced dropbox.js).
// The library is Tony's Mac folder ~/CTH/<app> (videos, diagrams, mindmaps or presentations).
// The Mac bridge (x-ai/bridge) keeps it in step with this app's own server
// (x-ai/worker: R2 + D1), and this module talks to that server at /api/fs/*.
// Paths are relative to the library root: '/Drills/clip.mp4'.
//
// Sign-in is the CTH password card in front of the site (worker/src/gate.js):
// the session is the gate cookie, plus the token in localStorage `cth_gate`
// sent as X-CTH-Gate (Safari and the Notion apps drop cookies inside embeds).
// A 401 means the session ended: the page reloads onto the password card.
//
// Same exports as the old dropbox.js so the app code did not have to change
// shape; Dropbox-only ones (app key, PKCE, embed hand-back) are gone.
// Keep this file identical in videos/, diagrams/, mindmaps/ x-ai/web and presentations/ x-ai/src/lib.

const API = '/api/fs';
export const DEFAULT_ROOT = '';
/**
 * Running as the Mac app (the app's server on localhost, e.g. Video's
 * server/local-server.mjs, which sets window.__CTH_LOCAL__): the same API straight
 * from the Mac folder. Every call carries X-CTH-Local, there is no sign-in, and
 * whole files go up in one request.
 */
export const local = !!globalThis.__CTH_LOCAL__;

/** Inside the CTH Hub on the Mac (its app rail frames the app with ?hub=1): the app keeps its full layout. */
export const inHub = (() => { try { return window.self !== window.top && new URLSearchParams(location.search).get('hub') === '1'; } catch (_) { return false; } })();
/** Running inside another page (a Notion embed). Not the CTH Hub, which shows the app as it is. */
export const embedded = (() => { try { return window.self !== window.top && !inHub; } catch (_) { return true; } })();
/** This browser keeps what the app saves (false in a strict sandbox). */
export const storageOk = (() => { try { localStorage.setItem('__cth', '1'); localStorage.removeItem('__cth'); return true; } catch (_) { return false; } })();

export function libraryRoot() { return DEFAULT_ROOT; }
export function normPath(p) {
  let s = String(p || '').trim().replace(/\\/g, '/');
  if (!s || s === '/') return '';
  if (!s.startsWith('/')) s = `/${s}`;
  return s.replace(/\/+/g, '/').replace(/\/+$/, '');
}
/** Always true: the password card stands in front of the whole site. */
export function connected() { return true; }

export class DbxError extends Error {
  constructor(msg, status, body) { super(msg); this.status = status; this.body = body; }
}
/** A request that never reached the server (offline, wifi dropped, DNS): worth retrying later. */
export const isNetworkError = (e) => e instanceof TypeError || /Failed to fetch|NetworkError|Load failed|network/i.test(String(e?.message || ''));

function gateToken() { try { return localStorage.getItem('cth_gate') || ''; } catch (_) { return ''; } }
let reloading = false;
function signedOut() {
  if (!reloading) { reloading = true; setTimeout(() => location.reload(), 50); }
  return new Error('NOT_CONNECTED');
}
function message(status, data) {
  if (data?.code === 'not_found') return data.error || 'That folder or file is not there any more.';
  if (data?.code === 'conflict') return data.error || 'Something with that name is already there.';
  if (status === 413) return 'That file is too big for one request.';
  return data?.error || `The server answered ${status}.`;
}

async function call(method, route, { json, body, query, headers = {}, raw = false, signal } = {}) {
  const u = new URL(`${API}${route}`, location.origin);
  if (query) for (const [k, v] of Object.entries(query)) if (v != null) u.searchParams.set(k, typeof v === 'string' ? v : JSON.stringify(v));
  const h = { ...headers };
  if (local) h['X-CTH-Local'] = '1';
  else { const t = gateToken(); if (t) h['X-CTH-Gate'] = t; }
  let b = body;
  if (json !== undefined) { h['Content-Type'] = 'application/json'; b = JSON.stringify(json); }
  const r = await fetch(u, { method, headers: h, body: b, credentials: 'same-origin', signal });
  if (r.status === 401 && !local) throw signedOut();
  if (raw && r.ok) return r;
  const text = await r.text();
  let data = null; try { data = JSON.parse(text); } catch (_) { data = null; }
  if (!r.ok) throw new DbxError(message(r.status, data), r.status, `${data?.code || ''} ${text}`);
  return data;
}

/** Everything in the library in one listing. */
export async function listAll() {
  const j = await call('GET', '/list');
  return j.entries;
}
export async function meta(path) { return call('GET', '/meta', { query: { path: normPath(path) } }); }

function parseMeta(h) { try { const m = JSON.parse(h || 'null'); return m && m.name ? { id: m.id, dir: false, name: m.name, path: m.path_display, lower: m.path_lower, size: m.size, rev: m.rev, modified: Date.parse(m.server_modified) || 0, created: Date.parse(m.client_modified) || 0 } : null; } catch (_) { return null; } }
export async function download(path) {
  const r = await call('GET', '/file', { query: { path: normPath(path) }, raw: true });
  return { bytes: new Uint8Array(await r.arrayBuffer()), meta: parseMeta(r.headers.get('Dropbox-API-Result')) };
}
export async function readText(path) { const { bytes, meta: m } = await download(path); return { text: new TextDecoder().decode(bytes), meta: m }; }
/** A picture for a library card: the file itself (the server makes no thumbnails). */
export async function thumbnail(path) { const r = await call('GET', '/file', { query: { path: normPath(path) }, raw: true }); return r.blob(); }

const SINGLE_MAX = 90 * 1024 * 1024;
const PART = 32 * 1024 * 1024;
const sizeOf = (b) => (typeof b === 'string' ? new TextEncoder().encode(b).length : (b.size ?? b.byteLength ?? b.length));

/**
 * Write a file. `rev` given: only if the file is still that revision (the server
 * refuses with a conflict otherwise, so an edit made elsewhere meanwhile is
 * never silently overwritten). No rev: a new file, never over an existing one,
 * unless `overwrite`.
 */
export async function upload(path, bytes, { rev = null, overwrite = false } = {}) {
  const mode = rev ? 'update' : overwrite ? 'overwrite' : 'add';
  if (!local && sizeOf(bytes) > SINGLE_MAX) return multipart(normPath(path), bytes, { mode, rev });
  return call('PUT', '/file', { query: { path: normPath(path), mode, rev }, body: bytes, headers: { 'Content-Type': 'application/octet-stream' } });
}
/** Upload any size, reporting progress 0..1. */
export async function uploadAny(path, bytes, { overwrite = true, onProgress } = {}) {
  const mode = overwrite ? 'overwrite' : 'add';
  if (local || sizeOf(bytes) <= SINGLE_MAX) { const m = await upload(path, bytes, { overwrite }); onProgress?.(1); return m; }
  return multipart(normPath(path), bytes, { mode, onProgress });
}
async function multipart(path, bytes, { mode, rev = null, onProgress } = {}) {
  const blob = bytes instanceof Blob ? bytes : new Blob([bytes]);
  const start = await call('POST', '/upload/start', { json: { path, mode, rev, type: blob.type || 'application/octet-stream' } });
  const parts = [];
  try {
    for (let off = 0, n = 1; off < blob.size; off += PART, n++) {
      const chunk = blob.slice(off, Math.min(blob.size, off + PART));
      for (let attempt = 0; ; attempt++) {
        try { parts.push(await call('PUT', '/upload/part', { query: { uploadId: start.uploadId, n: String(n), plan: start.plan }, body: chunk })); break; }
        catch (e) { if (attempt >= 2 || !isNetworkError(e)) throw e; await new Promise((r) => setTimeout(r, 1500 * (attempt + 1))); }
      }
      onProgress?.(Math.min(0.99, (off + chunk.size) / blob.size));
    }
  } catch (e) { call('POST', '/upload/abort', { json: { uploadId: start.uploadId, plan: start.plan } }).catch(() => {}); throw e; }
  const m = await call('POST', '/upload/finish', { json: { uploadId: start.uploadId, plan: start.plan, parts } });
  onProgress?.(1);
  return m;
}

export async function createFolder(path) { return call('POST', '/folder', { json: { path: normPath(path) } }); }
export async function ensureFolder(path) { return call('POST', '/folder', { json: { path: normPath(path), ok_if_exists: true } }); }
export async function move(from, to) { return call('POST', '/move', { json: { from: normPath(from), to: normPath(to) } }); }
export async function copy(from, to) { return call('POST', '/copy', { json: { from: normPath(from), to: normPath(to) } }); }
/** Deleted files stay 30 days in the server's trash, and on the Mac they go to the Trash. */
export async function remove(path) { return call('POST', '/delete', { json: { path: normPath(path) } }); }

/** Change feed: a cursor now, then a long-poll that resolves when something changed after it. */
export async function cursorFor() { return (await call('GET', '/cursor')).cursor; }
export async function longpoll(cursor) {
  const j = await call('GET', '/changes', { query: { since: String(cursor), wait: '25' } });
  return { changes: !!(j.reset || j.changes.length), backoff: 0 };
}

// ---- video streaming -------------------------------------------------------------------
// A signed 4-hour URL that needs no cookie (a <video> inside an embed has none)
// and honours HTTP Range (the <video> element and the scrub decoder stream from it).
const links = new Map();
export async function tempLink(path, { fresh = false } = {}) {
  const p = normPath(path).toLowerCase();
  const hit = links.get(p);
  if (!fresh && hit && Date.now() < hit.until) return hit.url;
  const j = await call('POST', '/link', { json: { path: normPath(path) } });
  links.set(p, { url: j.url, until: Date.now() + 3 * 3600e3 });
  return j.url;
}
/** Bytes [offset, offset + length) of a file, through its signed link. */
export async function readRange(path, offset, length, retry = 1) {
  const url = await tempLink(path);
  const r = await fetch(url, { headers: { Range: `bytes=${offset}-${offset + length - 1}` } });
  if ((r.status === 403 || r.status === 410 || r.status === 404) && retry) { await tempLink(path, { fresh: true }); return readRange(path, offset, length, retry - 1); }
  if (r.status !== 206 && r.status !== 200) throw new Error(`Range read failed (${r.status})`);
  const buf = new Uint8Array(await r.arrayBuffer());
  return r.status === 200 ? buf.subarray(offset, offset + length) : buf;
}

/** What the Mac bridge still has to upload: { pending: [{ path, size }], current: { path, pct } | null, at, now }. */
export async function syncStatus() { return call('GET', '/syncstatus'); }

// ---- sibling apps (read only) -----------------------------------------------------------
// The server's READS setting lists the apps this one may read (Presentations reads
// diagrams, mindmaps and videos). `app` omitted or this app: this app's own files.
/** Every file in another app's library. */
export async function listApp(app) { return (await call('GET', '/list', { query: { app } })).entries; }
/** A file's details by id (survives renames and moves) or path, in this app or a sibling. */
export async function metaOf({ app, id, path }) { return call('GET', '/meta', { query: { app, id, path: path ? normPath(path) : undefined } }); }
/** A file's bytes by id or path, in this app or a sibling. */
export async function downloadOf({ app, id, path }) {
  const r = await call('GET', '/file', { query: { app, id, path: path ? normPath(path) : undefined }, raw: true });
  return { bytes: new Uint8Array(await r.arrayBuffer()), meta: parseMeta(r.headers.get('Dropbox-API-Result')) };
}
/** A signed 4-hour link by id (preferred) or path. Resolves { url, id, path, size }. */
const refLinks = new Map();
export async function linkOf({ app, id, path }, { fresh = false } = {}) {
  const k = `${app || ''}|${id || ''}|${(path || '').toLowerCase()}`;
  const hit = refLinks.get(k);
  if (!fresh && hit && Date.now() < hit.until) return hit.v;
  const j = await call('POST', '/link', { json: { app, id, path: path ? normPath(path) : undefined } });
  const v = { url: j.url, id: j.id, path: j.path, size: j.size };
  refLinks.set(k, { v, until: Date.now() + 3 * 3600e3 });
  return v;
}
